import { useState } from "react";
import { useNavigate } from "react-router";
import { Bell, CheckCheck } from "lucide-react";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import {
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useNotifications,
  useUnreadCount,
} from "@/features/notification/api";
import type { AppNotification } from "@/features/notification/types";
import { NotificationIcon } from "@/features/notification/notification-icon";

/** Compact Vietnamese relative time ("5 phút trước"). */
function timeAgo(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const diffMs = Date.now() - then;
  const min = Math.round(diffMs / 60000);
  if (min < 1) return "vừa xong";
  if (min < 60) return `${min} phút trước`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr} giờ trước`;
  const day = Math.round(hr / 24);
  return `${day} ngày trước`;
}

/**
 * The "your turn" bell. Polls the unread count (every 30s, via the query
 * hook); the dropdown lazily fetches the recent list when opened. Clicking a
 * notification marks it read and deep-links to the related appointment.
 */
export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const unread = useUnreadCount();
  const list = useNotifications(open);
  const markRead = useMarkNotificationRead();
  const markAll = useMarkAllNotificationsRead();

  const count = unread.data ?? 0;
  const items = list.data ?? [];

  const handleOpen = (n: AppNotification) => {
    if (!n.isRead) markRead.mutate(n.id);
    setOpen(false);
    if (n.entityType === "APPOINTMENT") {
      navigate(`/appointments/${n.entityId}`);
    }
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={count > 0 ? `Thông báo (${count} chưa đọc)` : "Thông báo"}
          className="relative flex size-9 items-center justify-center rounded-lg text-ink-soft transition-colors hover:bg-muted hover:text-foreground"
        >
          <Bell className="size-[18px]" aria-hidden="true" />
          {count > 0 ? (
            <span className="absolute -right-0.5 -top-0.5 inline-flex min-w-4 items-center justify-center rounded-full bg-primary px-1 font-mono text-[11px] leading-none text-primary-foreground font-semibold">
              {count > 9 ? "9+" : count}
            </span>
          ) : null}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 p-0">
        <div className="flex items-center justify-between border-b border-border px-3 py-2">
          <span className="text-[12px] font-semibold">Thông báo</span>
          {count > 0 ? (
            <button
              type="button"
              onClick={() => markAll.mutate()}
              className="flex items-center gap-1 text-[11px] font-medium text-primary hover:underline"
            >
              <CheckCheck className="size-3.5" aria-hidden="true" />
              Đọc tất cả
            </button>
          ) : null}
        </div>
        <div className="max-h-[60vh] overflow-y-auto">
          {list.isPending && open ? (
            <p className="px-3 py-6 text-center text-[12px] text-muted-foreground">
              Đang tải…
            </p>
          ) : items.length === 0 ? (
            <p className="px-3 py-6 text-center text-[12px] text-muted-foreground">
              Chưa có thông báo nào.
            </p>
          ) : (
            <ul className="flex flex-col">
              {items.map((n) => (
                <li key={n.id}>
                  <button
                    type="button"
                    onClick={() => handleOpen(n)}
                    className={cn(
                      "flex w-full items-start gap-2.5 border-b border-border px-3 py-2.5 text-left transition-colors last:border-b-0 hover:bg-muted/60",
                      n.isRead ? "opacity-70" : "bg-primary/5",
                    )}
                  >
                    <NotificationIcon type={n.type} size="sm" />
                    <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                      <div className="flex items-center gap-2">
                        <span className="flex-1 truncate text-[12px] font-semibold">
                          {n.title}
                        </span>
                        <span className="shrink-0 font-mono text-[11px] text-ink-faint">
                          {timeAgo(n.createdAt)}
                        </span>
                      </div>
                      {n.message ? (
                        <span className="line-clamp-2 text-[12px] text-muted-foreground">
                          {n.message}
                        </span>
                      ) : null}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        <button
          type="button"
          onClick={() => {
            setOpen(false);
            navigate("/notifications");
          }}
          className="w-full border-t border-border px-3 py-2 text-center text-[12px] font-medium text-primary hover:bg-muted/60"
        >
          Xem tất cả
        </button>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
